<!-- ===== TOP BAR SHELL ===== -->
<div class="flex bg-white overflow-x-auto items-center drop-shadow border-b relative z-[50]"
    [ngClass]="{'p-2': view() !== ContractTopBarView.COMPARE}">

    <ng-container
        [ngTemplateOutlet]="view() === ContractTopBarView.HOME ? homeTitleTpl : (view() === ContractTopBarView.COMPARE ? compareTitleTpl : versionsTitleTpl)">
    </ng-container>

    <div class="flex items-center gap-2 ml-auto flex-shrink-0">
        @if(view() === ContractTopBarView.HOME) {
        <ng-container [ngTemplateOutlet]="homeControlsTpl"></ng-container>
        }
        <ng-container [ngTemplateOutlet]="endIconsTpl"></ng-container>
    </div>
</div>


<!-- ===== TEMPLATE: Compare – Title ===== -->
<ng-template #compareTitleTpl>
    <div class="flex items-center gap-3">
        <app-breadcrumb></app-breadcrumb>
    </div>
</ng-template>


<!-- ===== TEMPLATE: Home – Title ===== -->
<ng-template #homeTitleTpl>
    <div class="flex items-center">
        @if(!isAnonymousUser()){
        <app-button [icon]="SVG.GO_BACK" [iconSource]="'svg'" [size]="'default'" variant="text" class="text-sm"
            (clicked)="goBack()">
        </app-button>
        }
        <div class="flex items-center gap-2">
            <span scrollReset class="max-w-[15rem] text-md block" [ngClass]="{
              'truncate cursor-pointer': !isEditingTitle,
              'overflow-x-auto whitespace-nowrap': isEditingTitle
            }" [pTooltip]="contract()?.name" tooltipPosition="top"
                [attr.contenteditable]="isOwner() || (isSameOrganization() && permissions().canEdit)"
                (click)="enableScroll()" (blur)="handleBlur($event)" (keydown)="onKeyDown($event)" tabindex="0">
                {{ contract()?.name }}
            </span>
            @if(externalDocument()){
            <app-icon [type]="SVG.EXTERNAL" [source]="'svg'" [size]="'sm'"
                [pTooltip]="isSameOrganization() ? 'Shared Externally' : 'Owned Externally'" />
            }
        </div>
    </div>
</ng-template>


<!-- ===== TEMPLATE: Versions – Title ===== -->
<ng-template #versionsTitleTpl>
    <div class="flex items-center gap-2">
        <app-button [icon]="SVG.GO_BACK" [iconSource]="'svg'" [size]="'default'" variant="text" class="text-sm"
            (clicked)="goBack()">
        </app-button>
        @if(selectedVersion()){
        <p class="text-md font-medium">
            {{ selectedVersion()!.name }} -
            {{ selectedVersion()!.createdAt | date: 'mediumDate' }}
            {{ selectedVersion()!.createdAt | date: 'shortTime' }}
        </p>
        } @else if(currentVersion()) {
        <p class="text-md font-medium">
            {{ currentVersion()!.name }} -
            {{ currentVersion()!.createdAt | date: 'mediumDate' }}
            {{ currentVersion()!.createdAt | date: 'shortTime' }}
        </p>
        }
    </div>
</ng-template>


<!-- ===== TEMPLATE: Home – Middle Controls ===== -->
<ng-template #homeControlsTpl>

    <app-credits-overview variant="chip"></app-credits-overview>

    <!-- Status -->
    <app-popover #statusMenu appendTo="body" [width]="'14rem'" class="p-0 m-0">
        <ng-template #trigger>
            @if(isSameOrganization()) {
            <app-button variant="outlined" severity="contrast" step-id="step1" [loading]="loadingStatuses()"
                class="border-none rounded-lg bg-blue-50 max-w-[7rem]" icon="pi pi-chevron-down"
                [iconPosition]="'right'" [iconSize]="'sm'"
                [pTooltip]="selectedStatus() ? selectedStatus()!.name + (!selectedStatus()!.active ? ' Deleted' : '') : 'Select Status'"
                tooltipPosition="bottom"
                [label]="selectedStatus() ? selectedStatus()!.name + (!selectedStatus()!.active ? ' Deleted' : '') : 'Select Status'" />
            }
        </ng-template>
        <ng-template #body>
            <div class="max-h-[25rem] overflow-y-auto p-2 min-w-[200px]" (click)="$event.stopPropagation()">
                @for(item of statusMenuItems(); track item.label) {
                <div class="px-4 py-2 cursor-pointer hover:bg-gray-100 rounded transition-colors"
                    [ngClass]="item['class']" (click)="statusItemClicked.emit({item, menu: statusMenu})">
                    {{ item.label }}
                    <span *ngIf="!item.active" class="text-danger">Deleted</span>
                </div>
                }
            </div>
        </ng-template>
    </app-popover>

    <!-- Doc Type -->
    <div class="max-w-[7rem] rounded-lg p-2.5 font-medium text-xs bg-gray-100 truncate"
        [pTooltip]="contract()?.linkedDocType?.name" tooltipPosition="top">
        {{contract()?.linkedDocType?.name}}
    </div>

    @if(contract()){
    <!-- Upload -->
    @if(!contract()!.containsForm){
    <app-button [icon]="SVG.UPLOAD" [iconSource]="'svg'" [size]="'default'" variant="outlined" [severity]="'contrast'"
        pTooltip="Upload" tooltipPosition="top" label="Upload" (clicked)="createDialogOpened.emit()"
        *ngIf="isOwner() || (isSameOrganization() && permissions().canEdit)" step-id="step2">
    </app-button>
    }

    <!-- Versions -->
    <app-button [icon]="SVG.VERSION" [iconSource]="'svg'" [size]="'default'" variant="outlined" [severity]="'contrast'"
        [pTooltip]="'Last updated: ' + (docUpdatedAt() | date:'mediumDate') + ' at ' + (docUpdatedAt() | date:'shortTime')"
        tooltipPosition="top" (clicked)="versionsHistoryToggled.emit()"
        *ngIf="isOwner() || (isSameOrganization() && permissions().canEdit)" step-id="step3">
    </app-button>

    <!-- Hidden datepicker anchor for programmatic trigger from overflow menu -->
    @if(currentRole() === CONTRACT_USER_ROLE.OWNER || isEditor()){
    <div class="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden">
        <app-datepicker #dueDatePicker [(value)]="selectedDueDate" [config]="{
                dateFormat: 'dd/mm/yy',
                minDate: currentDate(),
                maxDate: projectDueDate() || undefined,
                showIcon: false,
                keepInvalid: true,
                showButtonBar: true,
                appendTo: 'body'
            }" (onSelect)="dueDateSelected.emit($event)" (onClear)="dueDateCleared.emit()">
            <ng-template pTemplate="date" let-date>
                <span [class.selected-past-date]="isSelectedPastDate(date)"
                    [class.past-date]="date.day < currentDate().getDate()">
                    {{date.day}}
                </span>
            </ng-template>
        </app-datepicker>
    </div>
    }

    <!-- Overflow menu: Project, Download, Due Date -->
    @if(overflowMenuItems().length > 0){
    <app-menu #overflowMenu [items]="overflowMenuItems()">
        <app-button #overflowMenuBtn variant="outlined" severity="contrast" icon="ellipsis-vertical" iconSource="lucide"
            pTooltip="More" tooltipPosition="top" (clicked)="overflowMenu.toggle($event)" step-id="step4">
        </app-button>
    </app-menu>
    }

    <!-- Refresh -->
    <app-button [icon]="'pi pi-refresh'" [iconSource]="'primeng'" [size]="'default'" [variant]="'outlined'"
        [severity]="'contrast'" pTooltip="Refresh" tooltipPosition="top" [disabled]="loading()"
        (clicked)="lastModifiedRequested.emit()">
    </app-button>
    }

    <!-- Review Button -->
    @if(contract() && showReviewBtn()){
    <app-button variant="outlined" severity="primary"
        [label]="isCurrentUserReviewedDoc() ? 'Mark as undone' : 'Mark as done'"
        (clicked)="reviewDocumentClicked.emit()" />
    }

    <!-- Share Doc -->
    @if(isOwner() || (isSameOrganization() && permissions().canEdit)){
    <div step-id="step5">
        <app-share-doc [contract]="contract()!" [projectId]="projectId()"
            [workspace]="currentUser()?.currentWorkspace?.toString() || null" />
    </div>
    }

    <!-- Action Button -->
    @if(contract()?.status != CONTRACT_STATUS.SIGNATURE_COMPLETED && !isAnonymousUser() && isSameOrganization()){
    <app-button variant="outlined" severity="primary" [label]="labelText()" [disabled]="actionButton()" variant="filled"
        (clicked)="actionTriggered.emit()" *ngIf="isOwner() || isEditor()" step-id="step6">
    </app-button>
    }
</ng-template>


<!-- ===== TEMPLATE: End Icons (both views) ===== -->
<ng-template #endIconsTpl>
    <div class="flex items-center gap-2">

        <!-- Credits: Compare view only -->
        @if(view() === ContractTopBarView.COMPARE){
        <app-credits-overview variant="chip"></app-credits-overview>
        }

        <!-- Subscription chip: Compare view only -->
        @if(view() === ContractTopBarView.COMPARE && subscriptionState.subscriptionDetails() &&
        subscriptionState.subscriptionDetails()?.plan && ENV.enableSubscription){
        @if(subscriptionState.isTrial()) {
        <app-button variant="outlined" severity="theme"
            [label]="'Trial ends in ' + subscriptionState.trialDaysLeftFormatted()"
            (clicked)="canAccessPricing() ? navigateToPricing() : null" />
        } @else {
        @if(subscriptionState.plan()?.name){
        <app-button variant="outlined" severity="theme" [label]="subscriptionState.plan()!.name"
            (clicked)="canAccessPricing() ? navigateToPricing() : null" [icon]="'pi pi-chevron-right'"
            [iconSource]="'primeng'" [iconPosition]="'right'" />
        }
        }
        }

        @if(!isAnonymousUser()){
        <div class="flex items-center justify-center w-9 h-9 bg-white border border-gray-200 rounded-lg">
            <app-notifications class="cursor-pointer"></app-notifications>
        </div>
        }

        @if(isSameOrganization()) {
        <!-- Desktop: Support & Assistance -->
        <div class="hidden xl:flex gap-2">
            <app-menu #support [items]="supportMenuItems()">
                <div class="flex items-center gap-1 p-[9px] border border-gray-200 rounded-lg"
                    [ngClass]="{'cursor-not-allowed opacity-50': isRecording(), 'cursor-pointer': !isRecording(), 'bg-primary': support.isVisible}"
                    (click)="support.toggle($event)" [pTooltip]="'Support'" tooltipPosition="left">
                    <app-icon [type]="TOP_BAR_SVG.SUPPORT" [source]="'svg'" [size]="'sm'" />
                    <app-icon [type]="support.isVisible ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                        [source]="'primeng'" [size]="'sm'" />
                </div>
            </app-menu>
        </div>

        <app-menu #info [items]="infoMenuItems()">
            <div class="flex items-center gap-1 cursor-pointer p-[9px] border border-gray-200 rounded-lg"
                [ngClass]="{'bg-primary': info.isVisible}" (click)="info.toggle($event)" [pTooltip]="'Assistance'"
                tooltipPosition="left">
                <app-icon [type]="TOP_BAR_SVG.ASSISTANCE" [source]="'svg'" [size]="'sm'" />
                <app-icon [type]="info.isVisible ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" [source]="'primeng'"
                    [size]="'sm'" />
            </div>
        </app-menu>

        <!-- Mobile: Hamburger (Only Home View) -->
        @if(view() === ContractTopBarView.HOME) {
        <div class="xl:hidden">
            <p-menu #mobileMenu [model]="mobileMenuItems" [popup]="true" appendTo="body"></p-menu>
            <div class="text-xs cursor-pointer p-[9px] border border-gray-200 rounded-lg"
                (click)="mobileMenu.toggle($event)">
                <app-icon [type]="'pi pi-ellipsis-v'" [source]="'primeng'" [size]="'sm'" />
            </div>
        </div>
        }
        }
    </div>
</ng-template>